import { usePutGroup } from '@vlabs/api/artemy/client';
import type { GroupRecord } from '@vlabs/api/artemy/model';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { checkGroupAvailability } from '@vlabs/monokit/entities/check-group-availability';

import type { AttachPermissionTagGroupProps } from '../types';

interface UseAttachPermissionTagGroupReturn {
  attachGroup: (group: GroupRecord) => Promise<'success' | 'cancelled' | 'error'>;
  isPending: boolean;
}

export function useAttachPermissionTagGroup({
  permissionTag,
}: AttachPermissionTagGroupProps): UseAttachPermissionTagGroupReturn {
  const { t } = useTranslation();
  const putGroup = usePutGroup();

  async function attachGroup(group: GroupRecord): Promise<'success' | 'cancelled' | 'error'> {
    const availability = await checkGroupAvailability({ groupId: group.group_id });

    if (availability === 'cancelled') {
      return 'cancelled';
    }

    try {
      await putGroup.mutateAsync({
        data: {
          analytics: group.analytics,
          description: group.description,
          group_name: group.group_name,
          owner_department: group.owner_department,
          permission_tag: permissionTag,
          stream_settings: group.stream_settings,
        },
        groupId: group.group_id,
      });

      toast.success(t('features:attach_permission_tag_group.on_success'));

      return 'success';
    } catch {
      toast.error(t('features:attach_permission_tag_group.on_error'));

      return 'error';
    }
  }

  return {
    attachGroup,
    isPending: putGroup.isPending,
  };
}
